import { Button, Modal, PropagationNotice, WriteError } from '@shared/ui';

import { overrideConfirmCopy, type OverrideChange } from './override-confirm-copy';

import type { FlagKind } from '../schemas/flag-type';

/**
 * The confirmation on a client's **first** override for a flag.
 *
 * Same shape and same words as `OverrideDialog`'s confirm step — it calls the
 * same `overrideConfirmCopy` (NOXSCRUM-1670), so a `salesforce` flag says here
 * exactly what it says there: the value is projected one-way into that
 * customer's own Salesforce org.
 *
 * Not a second confirmation contract, and deliberately not a stricter one.
 * `tenant-flag-row.ts` decides when this opens, and it opens on exactly the
 * case the flag screen stops for: creating the row. Toggling a row that exists
 * and reverting one to the default both write without asking, there and here.
 *
 * `pending` is `null` while closed. Setting it opens the dialog *instead of*
 * writing — the switch's `onChange` no longer mutates, this dialog's confirm
 * button does, in the parent. Cancelling needs no rollback: the mutation was
 * never started, so the switch renders the value it already had.
 */
export function TenantOverrideConfirmDialog({
    flagKey,
    kind,
    pending,
    isPending,
    error,
    onCancel,
    onConfirm,
}: {
    flagKey: string;
    kind: FlagKind;
    pending: OverrideChange | null;
    isPending: boolean;
    error: Error | null;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    const copy = pending ? overrideConfirmCopy(flagKey, kind, pending) : undefined;

    return (
        <Modal
            open={pending !== null}
            onClose={() => {
                if (!isPending) onCancel();
            }}
            dismissable={!isPending}
            title={copy?.title ?? ''}
            description={copy?.description}
            footer={
                <>
                    <Button variant="ghost" onClick={onCancel} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button disabled={isPending} onClick={onConfirm}>
                        {isPending ? 'Saving…' : 'Confirm'}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4">
                {error && <WriteError error={error} />}
                <PropagationNotice />
            </div>
        </Modal>
    );
}
